import {faro} from '@grafana/faro-react';
import {QueryErrorResetBoundary} from '@tanstack/react-query';
import {ErrorBoundary} from 'react-error-boundary';
import {AppError, ERROR_KIND_CONFIG, Icon, extractErrorMessage} from 'shared';

import type {FallbackProps} from 'react-error-boundary';

const KIND_TEXT_CLASS: Record<string, string> = {
  critical: 'text-tomato11',
  warning: 'text-amber11',
  informative: 'text-blue11',
};

function DefaultFallback({
  error,
  resetErrorBoundary,
}: FallbackProps): React.JSX.Element {
  const kind = error instanceof AppError ? error.kind : 'critical';
  const {icon, iconClassName} = ERROR_KIND_CONFIG[kind];
  const textClass = KIND_TEXT_CLASS[kind];

  return (
    <button onClick={resetErrorBoundary} className={textClass}>
      <Icon icon={icon} className={`mr-2 ${iconClassName}`} />
      {extractErrorMessage(error)} Click to retry data fetching.
    </button>
  );
}

type Props = {
  fallback?: (fallbackProps: FallbackProps) => React.ReactElement;
  children: React.ReactNode;
};
export function AppErrorBoundary(props: Props): React.JSX.Element {
  return (
    <QueryErrorResetBoundary>
      {({reset}) => (
        <ErrorBoundary
          fallbackRender={(fallbackProps) =>
            props.fallback?.(fallbackProps) || (
              <DefaultFallback {...fallbackProps} />
            )
          }
          onError={(error, info) =>
            faro.api?.pushError(
              error instanceof Error ? error : new Error(String(error)),
              {context: {componentStack: info.componentStack ?? ''}}
            )
          }
          onReset={reset}
        >
          {props.children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

AppErrorBoundary.displayName = 'AppErrorBoundary';
